import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import { useBoardChartData } from '../../hooks/useBoardChartData';
import { BaseChartProps } from './types';
import { CHART_COLORS } from './chartTheme';
import { useSetChartMetadata } from './ChartContext';

export const BillingIssuesTimelineChart: React.FC<BaseChartProps> = () => {
    const { t } = useTranslation();
    const { events, isLoading } = useBoardChartData();

    const billingEvents = useMemo(() => {
        if (!events) return [];
        return events
            .filter((e: any) => (e.type || e.eventType)?.includes('billing') || (e.type || e.eventType)?.includes('funding'))
            .sort((a: any, b: any) => (b.date || '').localeCompare(a.date || ''))
            .slice(0, 50);
    }, [events]);

    const isEmpty = !isLoading && billingEvents.length === 0;
    useSetChartMetadata({
        isEmpty,
        emptyTitle: t('charts.noBillingIssues', 'No Billing Issues'),
        emptyDescription: t('charts.noBillingIssuesDesc', 'No billing events detected in this period'),
    });

    if (isEmpty) return null;

    return (
        <div className="w-full h-full bg-transparent overflow-hidden flex flex-col">
            <div className="flex-1 min-h-0 p-3 overflow-y-auto">
                {billingEvents.length > 0 && (
                    <div className="space-y-2">
                        {billingEvents.map((event: any, idx: number) => {
                            const isFailed = (event.type || event.eventType)?.includes('failed') || (event.type || event.eventType)?.includes('chargeback');
                            const isRefund = (event.type || event.eventType)?.includes('refund');
                            const color = isFailed ? CHART_COLORS.danger : isRefund ? CHART_COLORS.warning : CHART_COLORS.revenue;
                            let dateLabel = '';
                            try { dateLabel = format(parseISO(event.date), 'MMM dd, HH:mm'); } catch { dateLabel = event.date || ''; }
                            return (
                                <div key={idx} className="flex items-start gap-2 py-1">
                                    <div className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0" style={{ backgroundColor: color }} />
                                    <div className="min-w-0 flex-1">
                                        <div className="text-[10px] font-medium truncate" style={{ color: 'var(--text-primary)' }}>{event.description || (event.type || event.eventType)}</div>
                                        <div className="text-[10px]" style={{ color: CHART_COLORS.gray[400] }}>{dateLabel}</div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

            </div>
        </div>
    );
};
